import { useGetMenuNavigationQuery } from '@/features/api/endpoints/menuNavigationEndpoints';
import type { MenuNavigation } from '@/models/types';
import { Breadcrumbs, Text } from '@mantine/core';

const getParentAsNode = (parent: MenuNavigation['parent']): MenuNavigation | null => {
  if (!parent || typeof parent === 'number') {
    return null;
  }

  return parent;
};

const buildAncestorLabels = (menuNavigation: MenuNavigation): string[] => {
  const labels: string[] = [];
  const visitedIds = new Set<number>();

  let currentParent = getParentAsNode(menuNavigation.parent);

  while (currentParent) {
    if (visitedIds.has(currentParent.id)) {
      break;
    }

    visitedIds.add(currentParent.id);

    if (currentParent.label) {
      labels.push(currentParent.label);
    }

    currentParent = getParentAsNode(currentParent.parent);
  }

  return labels.reverse();
};

type MenuNavigationBreadcrumbProps = {
  id: number;
};

const MenuNavigationBreadcrumb = ({ id }: MenuNavigationBreadcrumbProps) => {
  const { data } = useGetMenuNavigationQuery({
    id,
    q: {
      fields: [
        '*',
        {
          parent: [
            '*',
            {
              parent: [
                '*',
                {
                  parent: ['*'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  if (!data) {
    return null;
  }

  const breadcrumbLabels = [...buildAncestorLabels(data), ...(data.label ? [data.label] : [])];

  if (breadcrumbLabels.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs separator="›">
      {breadcrumbLabels.map(label => (
        <Text key={label} component="span" inherit>
          {label}
        </Text>
      ))}
    </Breadcrumbs>
  );
};

export default MenuNavigationBreadcrumb;
